/**
* automatic-response.util.ts
*
* Frontend mirror of `AutomaticResponseUtil` in `@app/trendmulti-core`. The two must stay in step:
* the screen offers the attachment and counts the caption, the backend refuses a response that
* breaks the rule.
*/
import { ChannelTypeEnum } from '../models/enum/channel.type';
import { MessageTypeEnum } from '../models/enum/message.type.enum';
import { UtilHelper } from './util.helper';

const MEDIA_CAPTION_MAX = 1024;

const ATTACHMENT_CHANNEL_TYPES: ReadonlySet<string> = new Set([
  ChannelTypeEnum.CLOUD_API,
  ChannelTypeEnum.Z_API,
]);

const ATTACHMENT_TYPE_BY_CONTENT_TYPE: Readonly<Record<string, MessageTypeEnum>> = {
  'image/jpeg': MessageTypeEnum.image,
  'image/png': MessageTypeEnum.image,
  'application/pdf': MessageTypeEnum.document,
  'application/msword': MessageTypeEnum.document,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': MessageTypeEnum.document,
  'application/vnd.ms-excel': MessageTypeEnum.document,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': MessageTypeEnum.document,
  'application/vnd.ms-powerpoint': MessageTypeEnum.document,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': MessageTypeEnum.document,
  'text/plain': MessageTypeEnum.document,
};

export class AutomaticResponseUtil {

  // `accept` of the file input for an image or a document: the same types the attendance sends.
  static accept(type: MessageTypeEnum): string {
    return Object.keys(ATTACHMENT_TYPE_BY_CONTENT_TYPE)
      .filter((contentType) => ATTACHMENT_TYPE_BY_CONTENT_TYPE[contentType] === type)
      .join(',');
  }

  static acceptsAttachment(channelType?: string): boolean {
    return ATTACHMENT_CHANNEL_TYPES.has(channelType ?? '');
  }

  // Only the Cloud API documents a caption limit; Z-API documents none.
  static captionLimit(channelType?: string): number | null {
    return channelType === ChannelTypeEnum.CLOUD_API ? MEDIA_CAPTION_MAX : null;
  }

  static attachmentType(contentType?: string): MessageTypeEnum | null {
    return ATTACHMENT_TYPE_BY_CONTENT_TYPE[contentType ?? ''] ?? null;
  }

  // The same size limits the attendance applies to an attachment.
  static maxSize(contentType?: string): number | null {
    return UtilHelper.getFileTypeLimit(contentType ?? '')?.maxSize ?? null;
  }

  // Upper-case extension shown on the document badge ('' when the name has none).
  static extension(filename?: string): string {
    const dot = filename?.lastIndexOf('.') ?? -1;
    return dot > 0 ? filename.slice(dot + 1, dot + 5).toUpperCase() : '';
  }
}
